import { useEffect, useState } from "react";
import { Smartphone, X, Share, Plus } from "lucide-react";

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null); // Android beforeinstallprompt event
  const [show, setShow] = useState(false);
  const [iosGuide, setIosGuide] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS: there's no event — show our own banner after a short delay
    if (isIos()) {
      const t = setTimeout(() => setShow(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    // Hide for THIS page view only — banner returns next visit/refresh
    setShow(false);
    setIosGuide(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        // Track install
        try {
          const api = (await import("../lib/api")).default;
          await api.post("/pwa/installed", { platform: "android" });
        } catch (_) { /* never block UX */ }
      }
      // Either way (accepted or not), hide for this page view
      dismiss();
      setDeferred(null);
    } else if (isIos()) {
      setIosGuide(true);
    }
  };

  // Detect iOS standalone-mode visit (best-effort install tracking on iOS)
  useEffect(() => {
    if (!isStandalone()) return;
    const KEY = "studict_ios_install_tracked";
    try {
      if (localStorage.getItem(KEY)) return;
      localStorage.setItem(KEY, "1");
      const platform = isIos() ? "ios" : "android";
      import("../lib/api").then((m) => {
        m.default.post("/pwa/installed", { platform }).catch(() => { /* ignore */ });
      });
    } catch (_) { /* ignore */ }
  }, []);

  if (!show) return null;

  return (
    <>
      {/* Bottom sheet banner */}
      {!iosGuide && (
        <div
          data-testid="pwa-install-banner"
          className="fixed bottom-4 inset-x-4 md:hidden z-40 nb-card p-4 bg-yellow-300 flex items-start gap-3"
          style={{ boxShadow: "6px 6px 0 0 #0F172A" }}
        >
          <div className="w-10 h-10 bg-white border-2 border-slate-900 rounded-md grid place-items-center shrink-0">
            <Smartphone className="w-5 h-5" strokeWidth={2.5}/>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-base font-black leading-tight">Install Studict on your phone</p>
            <p className="text-xs font-medium text-slate-700 mt-0.5">One-tap launch, full-screen, push reminders.</p>
            <div className="mt-2 flex gap-2">
              <button
                data-testid="pwa-install-btn"
                onClick={install}
                className="bg-slate-900 text-white text-xs font-bold px-3 py-1.5 rounded-md border-2 border-slate-900"
              >
                {isIos() ? "Show me how" : "Install"}
              </button>
              <button
                data-testid="pwa-install-dismiss"
                onClick={dismiss}
                className="bg-white text-xs font-bold px-3 py-1.5 rounded-md border-2 border-slate-900"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            aria-label="Dismiss"
            onClick={dismiss}
            className="shrink-0 w-7 h-7 grid place-items-center bg-white border-2 border-slate-900 rounded-md"
          >
            <X className="w-4 h-4" strokeWidth={2.5}/>
          </button>
        </div>
      )}

      {/* iOS step-by-step modal */}
      {iosGuide && (
        <div
          data-testid="pwa-ios-guide"
          className="fixed inset-0 z-50 bg-slate-900/60 grid place-items-end md:place-items-center p-0 md:p-4"
          onClick={dismiss}
        >
          <div
            className="w-full md:max-w-md bg-white border-2 border-slate-900 rounded-t-2xl md:rounded-2xl p-6"
            style={{ boxShadow: "8px 8px 0 0 #0F172A" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-xl font-black">Install on iPhone</h3>
              <button onClick={dismiss} aria-label="Close" className="w-8 h-8 grid place-items-center bg-white border-2 border-slate-900 rounded-md">
                <X className="w-4 h-4" strokeWidth={2.5}/>
              </button>
            </div>
            <p className="text-sm font-medium text-slate-700 mt-2">Three taps and Studict lives on your home screen.</p>
            <ol className="mt-5 space-y-4 text-sm">
              <li className="flex gap-3 items-start">
                <span className="font-display text-2xl font-black w-7 shrink-0">1</span>
                <div className="flex-1">
                  Tap the <span className="inline-flex items-center gap-1 bg-slate-100 border-2 border-slate-900 px-2 py-0.5 rounded font-bold"><Share className="w-3 h-3" strokeWidth={2.5}/> Share</span> icon at the bottom of Safari.
                </div>
              </li>
              <li className="flex gap-3 items-start">
                <span className="font-display text-2xl font-black w-7 shrink-0">2</span>
                <div className="flex-1">
                  Scroll the share sheet and tap <span className="inline-flex items-center gap-1 bg-slate-100 border-2 border-slate-900 px-2 py-0.5 rounded font-bold"><Plus className="w-3 h-3" strokeWidth={2.5}/> Add to Home Screen</span>.
                </div>
              </li>
              <li className="flex gap-3 items-start">
                <span className="font-display text-2xl font-black w-7 shrink-0">3</span>
                <div className="flex-1">Tap <strong>Add</strong> in the top right.</div>
              </li>
            </ol>
            <p className="text-xs text-slate-500 mt-5">Tip: this only works in Safari — not Chrome, Firefox or in-app browsers (e.g. Instagram).</p>
            <button onClick={dismiss} className="nb-btn-primary mt-5 w-full">Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
